<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted } from "vue";
import { useMainStore } from "../store/main";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { DateTime } from "luxon";
import Decision from "../classes/Decision";
import EntityField from "./EntityField.vue";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import { useKeyboardActions } from "../composables/keyboardActions";
import { useValidation } from "../composables/validation";
import PGRESTResult from "../classes/PGRESTResult";
import Auction from "../classes/DB_Entities/Auction";
import { ElForm, ElRow, ElCol, ElFormItem, ElButton } from "element-plus";
import AcceptAllPending from "./AcceptAllPending.vue";
import UserType from "../enums/UserType";

const route = useRoute();
const router = useRouter();
const mainStore = useMainStore();
const pgrestapi = usePGRESTAPIStore();
const {
  validationErrors,
  onValidationChange,
  checkValidationsForError,
} = useValidation();
const keyActions = useKeyboardActions();
const { t } = useI18n({ useScope: "global" });

const debtorsList = ref(new Array<any>());
const realEstatesList = ref(new Array<any>());
const RECollateralsList = ref(new Array<any>());
const PreviousStatusList = ref(new Array<any>());
const agentsList = ref<any>([]);
let viewOnly = ref<boolean>(false);

const {
  cleanObject,
  data,
  fullObj,
  hasPendingChanges,
  isLoading,
  table_name,
  clickAcceptAll,
  loadData,
  resetObjects,
  save,
  showError,
  showSuccess,
  showWarning,
  updateDecision,
} = useGenericMethodsVariables();

watch(
  () => fullObj.value.debtor_id?.originalValue,
  async (newVal, oldVal) => {
    if (data.value.debtor_id !== fullObj.value.debtor_id?.originalValue && fullObj.value.debtor_id.originalValue !== undefined) {
      fullObj.value.real_estate_id.originalValue = "";
    }

    if (newVal === undefined) return;

    await loadRealEstates(newVal);
  }
);

watch(
  () => fullObj.value.real_estate_id?.originalValue,
  async (newVal, oldVal) => {
    if (data.value.real_estate_id !== fullObj.value.real_estate_id?.originalValue && fullObj.value.debtor_id.originalValue !== undefined)
      fullObj.value.re_collateral_id.originalValue = "";

    if (newVal === undefined) return;

    await loadRECollaterals(newVal);
  }
);

onMounted(async () => {
  isLoading.value = true;
  table_name.value = Auction.getTableNameStatic();
  cleanObject.value = new Auction();

  console.log("auction_id", route.params.auction_id);
  console.log("view_only", route.params.view_only);

  await loadSelects();
  await loadDebtors();
  await loadAgents();

  resetObjects();

  if (route.params.auction_id === "new") {
    isLoading.value = false;
    return;
  }

  if (route.params.view_only === "true")
    viewOnly.value = true;

  let auction_id = parseInt(route.params.auction_id as string);

  await loadData(auction_id);
  await fixPendingSelects();

  isLoading.value = false;
});

const loadDebtors = async () => {
  debtorsList.value = [];
  let query = `view_mosdebtors_view1?approve_status=eq.1&order=fullname.asc,vat_number.asc`;

  if (mainStore.loggedUser.user_type === UserType.Agent) {
    query += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type === UserType.Supervisor) {
    query += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }

  let result: PGRESTResult = await pgrestapi.get(query);

  for (let i = 0; i < result.data.length; i++) {
    debtorsList.value.push({
      value: result.data[i].id,
      label: result.data[i].cid + " (" + result.data[i].fullname + ")",
    });
  }
};

const loadRealEstates = async (debtor_id: number) => {
  // console.log("loading real estates for debtor_id", debtor_id);
  let result: PGRESTResult = new PGRESTResult();
  realEstatesList.value = [];

  if (
    fullObj.value.debtor_id === undefined ||
    fullObj.value?.debtor_id?.originalValue === "" ||
    fullObj.value?.debtor_id?.originalValue === 0
  )
    return;

  result = await pgrestapi.get(
    "real_estate?select=id,arithmos_akinitou_diax&approve_status=eq.1&order=arithmos_akinitou_diax.asc&debtor_id=eq." +
    debtor_id
  ); // Only approved entities

  if (result.error) {
    showError(result.error.message);
    return;
  }
  // console.log(JSON.stringify(result.data));
  for (let i = 0; i < result.data.length; i++) {
    // console.log("pushing RE", JSON.stringify(result.data[i]));
    realEstatesList.value.push({
      value: result.data[i].id,
      label: result.data[i].arithmos_akinitou_diax,
    });
  }
};

const loadRECollaterals = async (real_estate_id: number) => {
  // console.log("loading RECollaterals for real_state_id", real_estate_id);
  let result: PGRESTResult = new PGRESTResult();
  RECollateralsList.value = [];

  if (
    fullObj.value?.real_estate_id?.originalValue === 0 ||
    fullObj.value?.real_estate_id?.originalValue === ""
  )
    return;

  result = await pgrestapi.get(
    "re_collaterals?select=id,ar_kalummatos&approve_status=eq.1&order=ar_kalummatos.asc&real_estate_id=eq." +
    real_estate_id
  ); // Only approved entities

  if (result.error) {
    showError(result.error.message);
    return;
  }

  for (let i = 0; i < result.data.length; i++) {
    // console.log("pushing", JSON.stringify(result.data[i]));
    RECollateralsList.value.push({
      value: result.data[i].id,
      label: result.data[i].ar_kalummatos,
    });
  }
};

const loadAgents = async () => {
  let result: PGRESTResult = new PGRESTResult();
  agentsList.value = [];

  result = await pgrestapi.get("config_agent?order=agent");

  if (result.error) {
    showError(result.error.message);
    return;
  }

  for (let i = 0; i < result.data.length; i++) {
    agentsList.value.push({
      value: result.data[i].id,
      label: result.data[i].agent,
    });
  }
};

const onDecision = async (decision: Decision) => {
  isLoading.value = true;

  await updateDecision(decision);

  await loadData(parseInt(route.params.auction_id as string));

  isLoading.value = false;
};

const onPreSave = async () => {
  // if (fullObj.value.hmer_prohg_pl?.originalValue === "") {
  //   showWarning(t("errors.missing_hmer_prohg_pl"));
  //   return;
  // }

  if (checkValidationsForError()) {
    showError("Please correct shown errors.");
    return;
  }

  isLoading.value = true;

  // Remove empty fields
  let fields = [
    "anag_apetisis",
    "yp_parat",
    "suntak_pinaka_katat",
    "hmer_anakophs",
    "hmer_ekd",
    "ekd_trapeza_ib",
  ];

  Object.keys(fullObj.value).forEach((key) => {
    if (fields.includes(key)) {
      if (
        fullObj.value[key].originalValue === "" ||
        fullObj.value[key].originalValue === undefined ||
        fullObj.value[key].originalValue === 0
      ) {
        delete fullObj.value[key];
      }
    }
  });

  delete fullObj.value.debtor;
  delete fullObj.value.real_estate;

  await save();
  isLoading.value = false;
  onClose();
};

const fixPendingSelects = async () => {
  for (let field of Object.keys(fullObj.value)) {
    if (fullObj.value[field].pendingValue === undefined) continue;

    let originalValue = fullObj.value[field].originalValue;
    let pendingValue = fullObj.value[field].pendingValue.value;

    // Debtor
    if (field === "debtor_id") {
      let debtor = await pgrestapi.get(`debtor?id=eq.` + pendingValue);
      fullObj.value[field].pendingValue["pendingDisplayValue"] =
        debtor.data[0].vat_number + " (" + debtor.data[0].fullname + ")";

      debtor = await pgrestapi.get(`debtor?id=eq.` + originalValue);
      fullObj.value[field].pendingValue["originalDisplayValue"] =
        debtor.data[0].vat_number + " (" + debtor.data[0].fullname + ")";
    }

    //Real Estate Nbr
    if (field === "real_estate_id") {
      let RealEstate = await pgrestapi.get(
        `real_estate?select=arithmos_akinitou_diax&id=eq.` + pendingValue
      );
      fullObj.value[field].pendingValue["pendingDisplayValue"] =
        RealEstate.data[0].arithmos_akinitou_diax;

      RealEstate = await pgrestapi.get(
        `real_estate?select=arithmos_akinitou_diax&id=eq.` + originalValue
      );
      fullObj.value[field].pendingValue["originalDisplayValue"] =
        RealEstate.data[0].arithmos_akinitou_diax;
    }

    //Re Collateral
    if (field === "re_collateral_id") {
      let ReCollateral = await pgrestapi.get(
        `re_collaterals?select=ar_kalummatos&id=eq.` + pendingValue
      );
      fullObj.value[field].pendingValue["pendingDisplayValue"] =
        ReCollateral.data[0].ar_kalummatos;
      ReCollateral = await pgrestapi.get(
        `re_collaterals?select=ar_kalummatos&id=eq.` + originalValue
      );
      fullObj.value[field].pendingValue["originalDisplayValue"] =
        ReCollateral.data[0].ar_kalummatos;
    }

    //Status
    if (field === "katastash") {
      fullObj.value[field].pendingValue[
        "pendingDisplayValue"
      ] = PreviousStatusList.value.find((element: any) => {
        return element.value === pendingValue;
      })?.label;
      fullObj.value[field].pendingValue[
        "originalDisplayValue"
      ] = PreviousStatusList.value.find((element: any) => {
        return element.value === originalValue;
      })?.label;
    }

    //Agents
    if (field === "ekd_trapeza_ib") {
      let agent = await pgrestapi.get(`config_agent?select=agent&id=eq.` + pendingValue);
      fullObj.value[field].pendingValue["pendingDisplayValue"] = agent.data[0].agent;
      agent = await pgrestapi.get(`config_agent?select=agent&id=eq.` + originalValue);
      fullObj.value[field].pendingValue["originalDisplayValue"] = agent.data[0].agent;
    }

    //Previous Auction Status

    if (field === "katastash_prohg_pl") {
      fullObj.value[field].pendingValue[
        "pendingDisplayValue"
      ] = PreviousStatusList.value.find((element: any) => {
        return element.value === pendingValue;
      })?.label;
      fullObj.value[field].pendingValue[
        "originalDisplayValue"
      ] = PreviousStatusList.value.find((element: any) => {
        return element.value === originalValue;
      })?.label;
    }

    // Convert to boolean
    if (field === "eparkes_pl" || field === "eisp_me_egg_lf") {
      fullObj.value[field].pendingValue.value =
        fullObj.value[field].pendingValue.value === "true";
    }
  }
};

const loadSelects = async () => {
  PreviousStatusList.value = [
    {
      value: "successful",
      label: t("AuctionsStatusList.successful"),
    },
    {
      value: "fruitless",
      label: t("AuctionsStatusList.fruitless"),
    },
    {
      value: "suspended",
      label: t("AuctionsStatusList.suspended"),
    },
    {
      value: "frustrated",
      label: t("AuctionsStatusList.frustrated"),
    },
    {
      value: "canceled",
      label: t("AuctionsStatusList.cancelled"),
    },
  ];
};

const checkAnagApaitisis = () => {
  let fv = false;

  try {
    fv =
      DateTime.fromFormat(fullObj.value.anag_apetisis.originalValue, "yyyy-MM-dd")
        .plus({ days: 5 })
        .diff(DateTime.fromFormat(fullObj.value.yp_parat.originalValue, "yyyy-MM-dd"), "days")
        .days > 5 ||
      DateTime.fromFormat(fullObj.value.anag_apetisis.originalValue, "yyyy-MM-dd")
        .plus({ days: 5 })
        .diff(DateTime.fromFormat(fullObj.value.yp_parat.originalValue, "yyyy-MM-dd"), "days")
        .days < 0;
  } catch (ex) {
    return false;
  }

  return fv;
};

const checkYpParat = () => {
  let fv = false;

  try {
    fv =
      DateTime.fromFormat(fullObj.value.yp_parat.originalValue, "yyyy-MM-dd")
        .plus({ days: 10 })
        .diff(
          DateTime.fromFormat(fullObj.value.suntak_pinaka_katat.originalValue, "yyyy-MM-dd"),
          "days"
        ).days > 10 ||
      DateTime.fromFormat(fullObj.value.yp_parat.originalValue, "yyyy-MM-dd")
        .plus({ days: 10 })
        .diff(
          DateTime.fromFormat(fullObj.value.suntak_pinaka_katat.originalValue, "yyyy-MM-dd"),
          "days"
        ).days < 0;
  } catch (ex) {
    return false;
  }

  return fv;
};

const checkHmerAnak = () => {
  let fv = false;

  try {
    DateTime.fromFormat(fullObj.value.suntak_pinaka_katat.originalValue, "yyyy-MM-dd")
      .plus({ days: 15 })
      .diff(
        DateTime.fromFormat(fullObj.value.hmer_anakophs.originalValue, "yyyy-MM-dd"),
        "days"
      ).days > 15 ||
      DateTime.fromFormat(fullObj.value.suntak_pinaka_katat.originalValue, "yyyy-MM-dd")
        .plus({ days: 15 })
        .diff(
          DateTime.fromFormat(fullObj.value.hmer_anakophs.originalValue, "yyyy-MM-dd"),
          "days"
        ).days < 0;
  } catch (ex) {
    return false;
  }

  return fv;
};

const onClose = () => {
  router.back();
};
</script>

<template>
  <div class="content-wrapper" v-loading="isLoading" style="margin: 30px; padding: 50px">
    <el-form>
      <!-- <el-row v-if="!isLoading">
        <el-col :span="24">
          <el-form-item class="btn-wrapper-components">
            <el-button type="primary" @click="onPreSave">{{$t('generic.save')}}</el-button>
            <el-button @click="onClose">{{$t('generic.close')}}</el-button>
          </el-form-item>
        </el-col>
      </el-row> -->
      <AcceptAllPending v-show="hasPendingChanges && !viewOnly"
        @accept="clickAcceptAll(parseInt(route.params.auction_id as string))" />
      <p />
      <el-row :gutter="10">
        <el-col :span="24">
          <EntityField type="select" :object="fullObj.debtor_id" label="Debtor" :options="debtorsList"
            @decision="onDecision" :validation-rules="[['^[0-9]*[1-9][0-9]*$', 'validation.select_debtor']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="select" :object="fullObj.real_estate_id" label="Real Estate" :options="realEstatesList"
            @decision="onDecision" :validation-rules="[['^[0-9]*[1-9][0-9]*$', 'validation.select_real_estate']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="select" :object="fullObj.re_collateral_id" label="RE Collateral" :options="RECollateralsList"
            @decision="onDecision" :validation-rules="[
                ['^[0-9]*[1-9][0-9]*$', 'validation.select_re_collateral'],
              ]" @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="input" label="Third Party ID" :object="fullObj.third_part_id" @decision="onDecision"
            :validation-rules="[['^[0-9]+$', 'validation.select_third_part_id']]" @validation-change="onValidationChange"
            :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="input" :label="$t('AuctionsDetails.auction_program_nbr')" :object="fullObj.ar_prog_pl"
            @decision="onDecision" :validation-rules="[['^[0-9]+$', 'validation.select_ar_prog_pl']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="datepicker" :label="$t('AuctionsDetails.auction_date')" :object="fullObj.hmer_pl"
            @decision="onDecision" :validation-rules="[['.+', 'validation.select_hmer_pl']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="datepicker" :label="$t('AuctionsDetails.declaration_of_claim')"
            :object="fullObj.anag_apetisis" @decision="onDecision"
            :validation-rules="[['.+', 'validation.select_anag_apetisis']]" @validation-change="onValidationChange"
            :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="input" :label="$t('AuctionsDetails.claimant')" :object="fullObj.epispeudon"
            @decision="onDecision" :validation-rules="[['^[0-9A-Za-zΑ-Ωα-ω ]+$', 'validation.select_epispeudon']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.auction_initial_price')" :object="fullObj.timh_pl"
            @decision="onDecision" :validation-rules="[['^[0-9]+(\,\d{1,2})?', 'validation.select_timh_pl']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="select" :object="fullObj.katastash" :label="$t('AuctionsDetails.auction_status')"
            :options="PreviousStatusList" @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.auction_price')" :object="fullObj.timh_katak"
            @decision="onDecision" :validation-rules="[['^[0-9]+(\,\d{1,2})?', 'validation.select_timh_katak']]"
            @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="input" :label="$t('AuctionsDetails.bidder')" :object="fullObj.pleiodotis"
            @decision="onDecision" @validation-change="onValidationChange" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="switch" :label="$t('AuctionsDetails.sufficient_auction_amount')" :object="fullObj.eparkes_pl"
            @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>

        <!-- <el-col :v-if="fullObj.eparkes_pl.originalValue"> -->
        <el-col v-if="fullObj.eparkes_pl?.originalValue">
          <el-col :span="24">
            <EntityField type="datepicker" :label="$t('AuctionsDetails.submission_of_comments')"
              :object="fullObj.yp_parat" @decision="onDecision" :validation-rules="[['.+', 'validation.select_yp_parat']]"
              @validation-change="onValidationChange" :is-disabled="viewOnly" />
            <div v-if="checkAnagApaitisis()" class="el-tag el-tag--danger">
              {{ $t("errors.ypovoli_paratiriseon") }}
            </div>
          </el-col>
          <el-col :span="24">
            <EntityField type="datepicker" :label="$t('AuctionsDetails.compiling_of_ranking_table')"
              :object="fullObj.suntak_pinaka_katat" @decision="onDecision"
              :validation-rules="[['.+', 'validation.select_yp_parat']]" @validation-change="onValidationChange"
              :is-disabled="viewOnly" />
            <div v-if="checkYpParat()" class="el-tag el-tag--danger">
              {{ $t("errors.suntaxi_pinaka_kat") }}
            </div>
          </el-col>
          <el-col :span="24">
            <EntityField type="datepicker" :label="$t('AuctionsDetails.date_of_suspension')"
              :object="fullObj.hmer_anakophs" @decision="onDecision"
              :validation-rules="[['.+', 'validation.select_auction_date']]" @validation-change="onValidationChange"
              :is-disabled="viewOnly" />
            <div v-if="checkHmerAnak()" class="el-tag el-tag--danger">
              {{ $t("errors.hmer_anakophs") }}
            </div>
          </el-col>
        </el-col>

        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.amount_attributed_to_client')"
            :object="fullObj.poso_ana_client" @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="switch" :label="$t('AuctionsDetails.collection_by_Letter_of_Guarantee')"
            :object="fullObj.eisp_me_egg_lf" @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>

        <el-col v-if="fullObj.eisp_me_egg_lf?.originalValue">
          <el-col :span="24">
            <EntityField type="select" :object="fullObj.ekd_trapeza_ib" :label="$t('AuctionsDetails.issuing_bank')"
              :options="agentsList" @decision="onDecision"
              :validation-rules="[['.+', 'validation.select_ekd_trapeza_ib']]" @validation-change="onValidationChange"
              :is-disabled="viewOnly" />
          </el-col>
          <el-col :span="24">
            <EntityField type="datepicker" :label="$t('AuctionsDetails.issue_date')" :object="fullObj.hmer_ekd"
              @decision="onDecision" :validation-rules="[['.+', 'validation.select_hmer_ekd']]"
              @validation-change="onValidationChange" :is-disabled="viewOnly" />
          </el-col>
          <el-col :span="24">
            <EntityField type="money" :label="$t('AuctionsDetails.lg_amount')" :object="fullObj.lg_amount"
              @decision="onDecision" :is-disabled="viewOnly" />
          </el-col>
          <el-col :span="24">
            <EntityField type="money" :label="$t('AuctionsDetails.lg_commission')" :object="fullObj.lg_comission"
              @decision="onDecision" :is-disabled="viewOnly" />
          </el-col>

          <el-col :span="24">
            <EntityField type="input" :label="$t('AuctionsDetails.beneficiary')" :object="fullObj.dikaiouxos_text"
              @decision="onDecision" :is-disabled="viewOnly" />
          </el-col>
          <el-col :span="24">
            <EntityField type="input" :label="$t('AuctionsDetails.maturity')" :object="fullObj.prothesmia_liksis"
              @decision="onDecision" :is-disabled="viewOnly" />
          </el-col>
        </el-col>
        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.attorney_fees')" :object="fullObj.eksoda_dikhgorou"
            @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.certified_appraisers_fee')"
            :object="fullObj.eksoda_pistop_ektim" @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="datepicker" :label="$t('AuctionsDetails.date_of_previous_auction')"
            :object="fullObj.hmer_prohg_pl" @decision="onDecision"
            :validation-rules="[['.+', 'validation.select_hmer_ekd']]" @validation-change="onValidationChange"
            :is-disabled="viewOnly" />
        </el-col>
        <el-col :span="24">
          <EntityField type="money" :label="$t('AuctionsDetails.price_of_previous_auction')"
            :object="fullObj.timh_prohg_pl" @decision="onDecision" :is-disabled="viewOnly" />
        </el-col>

        <el-col :span="24">
          <EntityField type="select" :object="fullObj.katastash_prohg_pl"
            :label="$t('AuctionsDetails.status_of_previous_auction')" :options="PreviousStatusList" @decision="onDecision"
            :is-disabled="viewOnly" />
        </el-col>
      </el-row>
      <el-row v-if="!isLoading">
        <el-col :span="24">
          <el-form-item class="btn-wrapper-components">
            <el-button v-if="!viewOnly" type="primary" @click="onPreSave">{{ $t('generic.save') }}</el-button>
            <el-button @click="onClose">{{ $t('generic.close') }}</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<style scoped></style>
